import { api } from './api';

export const PROFESSIONAL_ROLES = ['DOCTOR', 'CLINIC_CARE_GIVER'];
export const professionalLabels = { DOCTOR: 'Médico', CLINIC_CARE_GIVER: 'Técnico de saúde' };

const requireClinicAdmin = (actor) => {
    if (!actor?.user?.uuid || actor.role?.name !== 'CLINIC_ADMIN' || actor.role.disabled
        || !actor.role.uuid || !actor.role.account?.uuid) throw new Error('PROFESSIONALS_ACCESS_DENIED');
    return { 'CHEART-ROLE': actor.role.uuid };
};
const verifyTarget = (actor, target) => {
    requireClinicAdmin(actor);
    if (!target?.role_uuid || !PROFESSIONAL_ROLES.includes(target.role)
        || target.account?.uuid !== actor.role.account.uuid) throw new Error('INVALID_PROFESSIONAL_TARGET');
};
const readUserData = (data) => {
    if (data == null) return {};
    if (typeof data !== 'object' || Array.isArray(data)) throw new Error('INVALID_PROFESSIONAL_DATA');
    return data;
};

export const getProfessionals = async (actor, {
    roles = PROFESSIONAL_ROLES, disabled = false, search = '', token = null, pageSize = 10, signal
} = {}) => {
    const headers = requireClinicAdmin(actor);
    if (!roles.length || roles.some((role) => !PROFESSIONAL_ROLES.includes(role))) throw new Error('INVALID_PROFESSIONAL_ROLE');
    Object.assign(headers, {
        'CHEART-QUERY-ROLE': JSON.stringify(roles), 'CHEART-QUERY-DISABLED': String(disabled),
        'PAGINATION-LIMIT': pageSize
    });
    if (token) headers['PAGINATION-TOKEN'] = token;
    // Clinic actors are scoped by the backend to their selected account; no account override.
    const query = search.trim();
    const { data, headers: pagination } = query
        ? await api.post('/cheart/users/search', { query }, { headers, signal })
        : await api.get('/cheart/users', { headers, signal });
    const total = Number(pagination['pagination-count']);
    if (!Array.isArray(data) || !Number.isInteger(total) || total < 0) throw new Error('INVALID_PROFESSIONAL_PAGE');
    for (const target of data) {
        verifyTarget(actor, target);
        if (!roles.includes(target.role) || Boolean(target.disabled) !== disabled) throw new Error('INVALID_PROFESSIONAL_PAGE');
    }
    return { rows: data, total, nextToken: pagination['pagination-token'] || null };
};

export const fetchProfessional = async (actor, target) => {
    verifyTarget(actor, target);
    const { data } = await api.post('/cheart/users/fetch', { role_uuid: target.role_uuid }, {
        headers: requireClinicAdmin(actor)
    });
    verifyTarget(actor, data);
    if (data.role_uuid !== target.role_uuid || data.role !== target.role) throw new Error('INVALID_PROFESSIONAL_TARGET');
    const userData = readUserData(data.user_data);
    return {
        name: data.name, phone: data.phone || '', userData,
        professionalCertificate: typeof userData.professionalCertificate === 'string' ? userData.professionalCertificate : '',
        speciality: typeof userData.speciality === 'string' ? userData.speciality : ''
    };
};

const findExistingAccess = async (actor, values, disabled = false) => {
    let token = null;
    let loaded = 0;
    const seen = new Set();
    do {
        const page = await getProfessionals(actor, {
            roles: [values.role], disabled, search: values.email, token, pageSize: 50
        });
        const found = page.rows.find((row) => row.email?.trim().toLowerCase() === values.email.trim().toLowerCase());
        if (found) return found;
        loaded += page.rows.length;
        if (loaded >= page.total) return null;
        token = page.nextToken;
        if (!token || seen.has(token)) throw new Error('INVALID_PROFESSIONAL_PAGE');
        seen.add(token);
    } while (token);
    return null;
};

export const createProfessional = async (actor, values) => {
    const headers = requireClinicAdmin(actor);
    if (!PROFESSIONAL_ROLES.includes(values.role)) throw new Error('INVALID_PROFESSIONAL_ROLE');
    const existing = await findExistingAccess(actor, values);
    if (existing) return { access: existing, recovered: true };
    // POST also reactivates profiles and replaces user_data when supplied.
    // Preserve existing metadata if this email already has a disabled profile.
    const disabled = await findExistingAccess(actor, values, true);
    const previous = disabled ? await fetchProfessional(actor, disabled) : null;
    const professionalData = {};
    for (const key of ['professionalCertificate', 'speciality']) {
        if (values[key]?.trim()) professionalData[key] = values[key].trim();
    }
    const payload = {
        name: values.name.trim(), email: values.email.trim(), role: values.role,
        ...(values.phone?.trim() ? { phone: values.phone.trim() } : {}),
        ...(Object.keys(professionalData).length
            ? { user_data: { ...previous?.userData, ...professionalData } } : {})
    };
    try {
        const { data } = await api.post('/cheart/users', payload, { headers });
        verifyTarget(actor, data);
        if (data.role !== values.role || data.disabled
            || data.email?.trim().toLowerCase() !== values.email.trim().toLowerCase()) throw new Error('INVALID_PROFESSIONAL_TARGET');
        return { access: data, recovered: false };
    } catch (error) {
        // Role creation commits before the notification email is sent.
        const confirmed = await findExistingAccess(actor, values).catch(() => null);
        if (confirmed) return { access: confirmed, recovered: true };
        throw error;
    }
};

export const reactivateProfessional = async (actor, target) => {
    verifyTarget(actor, target);
    return createProfessional(actor, { name: target.name, email: target.email, role: target.role });
};

export const updateProfessional = async (actor, target, values) => {
    verifyTarget(actor, target);
    // Re-read immediately before PATCH: user_data is replaced, not merged, by the API.
    const latest = await fetchProfessional(actor, target);
    return api.patch('/cheart/users', {
        role_uuid: target.role_uuid, name: values.name.trim(), phone: values.phone.trim(),
        user_data: {
            ...latest.userData,
            professionalCertificate: values.professionalCertificate.trim(),
            speciality: values.speciality.trim()
        }
    }, { headers: requireClinicAdmin(actor) });
};

export const disableProfessional = async (actor, target) => {
    verifyTarget(actor, target);
    if (target.user_uuid === actor.user.uuid) throw new Error('CANNOT_DISABLE_SELF');
    return api.delete('/cheart/users', {
        data: { role_uuid: target.role_uuid }, headers: requireClinicAdmin(actor)
    });
};
